import type { Country } from './types'

/**
 * Сервис для управления телефонными масками стран
 * Читает данные из meta параметров страницы
 */
export class CountryService {
  private countries: Country[] = []

  /**
   * Преобразовать объект маски из meta в внутренний формат
   * @param {unknown} rawMask - Объект маски в произвольном формате
   * @return {Country | null} Нормализованный объект страны или null
   */
  private normalizeMask(rawMask: unknown): Country | null {
    if (!rawMask || typeof rawMask !== 'object') {
      return null
    }

    const rawCountry = (rawMask as Record<string, unknown>).country
    const rawPattern = (rawMask as Record<string, unknown>).pattern

    if (typeof rawCountry !== 'string' || typeof rawPattern !== 'string') {
      return null
    }

    const codeMatch = rawPattern.match(/^\+?(\d+)/)
    if (!codeMatch) {
      return null
    }

    return {
      country: rawCountry,
      pattern: rawPattern,
      code: codeMatch[1]
    }
  }

  /**
   * Загрузить единственную маску из meta-тегов
   * @return {Promise<Country[]>} Promise, возвращающий массив масок
   */
  public async fetchCountries(): Promise<Country[]> {
    try {
      const raw =
        document
          .querySelector<HTMLMetaElement>('meta[name="app:phone_masks"]')
          ?.content?.trim() || ''

      if (!raw) {
        this.countries = []
        return this.countries
      }

      const patterns: unknown = JSON.parse(raw)

      if (!Array.isArray(patterns)) {
        this.countries = []
        return this.countries
      }

      // Порядок важен: первая маска — страна текущей страницы, она же
      // используется как маска по умолчанию, пока номер не введён.
      this.countries = patterns
        .map((pattern) => this.normalizeMask({ country: 'DEFAULT', pattern }))
        .filter((country): country is Country => country !== null)

      return this.countries
    } catch (error) {
      console.error('Не удалось прочитать телефонные meta-параметры:', error)
      this.countries = []
      return this.countries
    }
  }

  /**
   * Получить все страны
   * @return {Country[]} Массив всех загруженных стран
   */
  public getCountries(): Country[] {
    return this.countries
  }

  /**
   * Найти страну по первым цифрам номера
   * При нескольких совпадениях выбирается самый длинный код
   * @param {string} digits - Номер, содержащий только цифры
   * @return {Country | undefined} Объект страны или undefined, если не найдена
   */
  public findCountryByDigits(digits: string): Country | undefined {
    let matchedCountry: Country | undefined

    for (const country of this.countries) {
      if (!digits.startsWith(country.code)) {
        continue
      }

      if (!matchedCountry || country.code.length > matchedCountry.code.length) {
        matchedCountry = country
      }
    }

    return matchedCountry
  }

  /**
   * Получить маску для первых цифр номера
   * @param {string} digits - Номер, содержащий только цифры
   * @return {string} Шаблон маски форматирования
   */
  public getPatternByDigits(digits: string): string {
    return this.findCountryByDigits(digits)?.pattern || ''
  }
}
